import { DateOnly } from "@shared/common/utils/luxonHelpers";
import { DateTime, Duration, Interval } from "luxon";
import { shared } from "use-broadcast-ts";
import { create } from "zustand";

interface SelectedTimeState {
  selectedTime?: DateTime<true>;
  scenarioInterval?: Interval<true>;
  moveToStart: () => void;
  moveToEnd: () => void;
  increase: (delta: Duration) => void;
  decrease: (delta: Duration) => void;
  /**
   * Sets the current date to the provided date, while maintaining the current time.
   */
  setDate: (date: DateOnly) => void;
  setDateTime: (datetime: DateTime) => void;
  setScenarioInterval: (scenarioInterval: Interval) => void;
}

export const useSelectedTimeStore = createSelectedTimeStore();

export function createSelectedTimeStore(channelName = "knop-selected-time") {
  return create<SelectedTimeState>()(
    shared(
      (set, get) => ({
        selectedTime: undefined,
        scenarioInterval: undefined,
        moveToStart: () =>
          set({
            selectedTime: get().scenarioInterval?.start,
          }),
        moveToEnd: () =>
          set({
            selectedTime: get().scenarioInterval?.end,
          }),
        increase: (delta: Duration) =>
          set({
            selectedTime: clamp(get().selectedTime?.plus(delta), get().scenarioInterval),
          }),
        decrease: (delta: Duration) =>
          set({
            selectedTime: clamp(get().selectedTime?.minus(delta), get().scenarioInterval),
          }),
        setDate: ({ year, month, day }: DateOnly) =>
          set({
            selectedTime: clamp(
              get().selectedTime?.set({ year, month, day }),
              get().scenarioInterval,
            ),
          }),
        setDateTime: (dateTime: DateTime) => {
          const selectedTime = clamp(roundToNearestPint(dateTime), get().scenarioInterval);
          const currentTime = get().selectedTime;
          if (selectedTime === currentTime || (selectedTime && currentTime?.equals(selectedTime))) {
            return;
          }
          set({ selectedTime });
        },
        setScenarioInterval: (scenarioInterval: Interval<true>) => {
          if (get().scenarioInterval?.equals(scenarioInterval)) {
            return;
          }
          set({ selectedTime: scenarioInterval.start, scenarioInterval });
        },
      }),
      {
        name: channelName,
        // Broadcasting serializes to JSON, so Luxon instances arrive as ISO strings.
        merge: (state, receivedState) => {
          const receivedSelectedTime = reviveDateTime(receivedState.selectedTime);
          const selectedTime =
            state.scenarioInterval &&
            receivedSelectedTime &&
            state.scenarioInterval.contains(receivedSelectedTime)
              ? receivedSelectedTime
              : state.selectedTime;

          return {
            ...state,
            ...receivedState,
            selectedTime,
            scenarioInterval: state.scenarioInterval,
          };
        },
      },
    ),
  );
}

function reviveDateTime(value: unknown): DateTime {
  let dateTime: DateTime | undefined;
  if (value instanceof DateTime) {
    dateTime = value;
  } else if (typeof value === "string") {
    dateTime = DateTime.fromISO(value);
  } else {
    dateTime = undefined;
  }

  return dateTime?.isValid ? dateTime : DateTime.invalid("Invalid DateTime");
}

function roundToNearestPint(dateTime: DateTime): DateTime {
  const milliseconds = 6_000;
  return DateTime.fromMillis(Math.round(dateTime.toMillis() / milliseconds) * milliseconds, {
    zone: dateTime.zone,
  });
}

function clamp(time?: DateTime, scenarioInterval?: Interval<true>): DateTime | undefined {
  if (!time || !scenarioInterval) {
    return undefined;
  }
  if (time < scenarioInterval.start) {
    return scenarioInterval.start;
  }
  if (time > scenarioInterval.end) {
    return scenarioInterval.end;
  }
  return time;
}
